import { convertDistance } from "@nova/format"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { BufferError, DistanceUnit, formattedDistanceUnit, LineWidth } from "@nova/constants"
import { BufferRange, Perimeter } from "@nova/protobuf"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { updatePerimeter } from "features/pubsub/entityActions/cubes/perimeter"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useMemo, useState } from "react"
import { useDebounceDispatch } from "tools/store"
import { setGlobalNotificationContext } from "user/profile/slice"
import { nextBufferColor } from "../module"
import { useAsyncCallback } from "@nova/use-async-callback"
import { updateCachedFirePerimeterBuffersProperties } from "features/entity-caching/fire-perimeters/buffers/slice"
import { testNewBufferDistance } from "@nova/calculate-buffers"

const MAX_PERIMETER_BUFFER_ATTEMPTS = 3

export const useFirePerimeterBuffers = (entity: Perimeter) => {
    const { debounceDispatch } = useDebounceDispatch(25)
    const dispatch = useAppDispatch()

    const workspaceDistanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const distanceUnit = (entity.buffers?.unit as DistanceUnit) || workspaceDistanceUnit
    const unitAbbreviation = formattedDistanceUnit[distanceUnit].abbrev

    const [bufferDistancesBeingUpdated, setBufferDistancesBeingUpdated] = useState(
        new Set<number>()
    )

    const buffers = useMemo(
        () =>
            entity.buffers &&
            [...entity.buffers?.range]
                .sort((a, b) => a.distance - b.distance)
                .map((buffer) => ({
                    ...buffer,
                    width: buffer.width as LineWidth,
                    distance: convertDistance(buffer.distance, DistanceUnit.Meters, distanceUnit),
                })),
        [entity.buffers, distanceUnit]
    )

    const updateBuffersArray = useCallback(
        (changes: {
            range?: { distance: number; color: number; width: LineWidth }[]
            unit?: DistanceUnit
        }) => {
            debounceDispatch(
                updatePerimeter({
                    id: entity.id,
                    updates: {
                        buffers: {
                            range:
                                changes?.range?.map((buffer) => ({
                                    ...buffer,
                                    distance: buffer.distance ?? 0,
                                })) ||
                                entity.buffers?.range ||
                                [],
                            unit: changes?.unit || distanceUnit,
                        },
                    },
                })
            )
        },
        [entity, debounceDispatch, distanceUnit]
    )

    const deleteBufferAtIndex = useCallback(
        (index: number) => {
            if (!buffers || (buffers && buffers.length === 0)) return

            const oldDistancesInMeters =
                buffers?.map((entry) => ({
                    ...entry,
                    distance: convertDistance(entry.distance, distanceUnit, DistanceUnit.Meters),
                })) || []

            const oldRange = JSON.parse(JSON.stringify(oldDistancesInMeters)) as BufferRange[]
            const newRange = oldRange.filter((_, i) => i !== index)

            updateBuffersArray({ range: newRange })
        },
        [buffers, distanceUnit, updateBuffersArray]
    )

    const editBufferAtIndex = useCallback(
        async (index: number, updates: { buffer?: number; color?: number; width?: LineWidth }) => {
            if (!buffers || (buffers && buffers.length === 0)) return

            if (updates.buffer === 0) {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Buffer distance must be non-zero.",
                        timeoutInMs: 4000,
                        status: GlobalNotificationStatus.error,
                    })
                )
                return
            }

            let hasBufferGenerationError = false
            const bufferBeingEdited = buffers.at(index)
            if (bufferBeingEdited && updates.buffer !== undefined) {
                setBufferDistancesBeingUpdated((set) => {
                    if (updates.buffer !== undefined)
                        return new Set([...set, bufferBeingEdited.distance])
                    return set
                })

                try {
                    await testNewBufferDistance(
                        entity.polygons,
                        {
                            ...bufferBeingEdited,
                            distance: convertDistance(
                                updates.buffer,
                                distanceUnit,
                                DistanceUnit.Meters
                            ),
                        },
                        (entity.buffers?.unit as DistanceUnit) || distanceUnit
                    )
                } catch (error) {
                    let errorMessage: string
                    switch (error) {
                        case BufferError.DistanceTooLarge:
                        case BufferError.Unknown:
                            errorMessage =
                                "Buffer distance exceeds the limit. Please enter a smaller value."
                            break
                        default:
                            errorMessage = "Failed to create buffer."
                            break
                    }

                    dispatch(
                        setGlobalNotificationContext({
                            message: errorMessage,
                            timeoutInMs: 4000,
                            status: GlobalNotificationStatus.error,
                        })
                    )

                    hasBufferGenerationError = true
                }
            }

            if (updates.buffer !== undefined)
                setBufferDistancesBeingUpdated((set) => {
                    if (bufferBeingEdited) {
                        set.delete(bufferBeingEdited.distance)
                        return new Set([...set])
                    }
                    return set
                })

            if (hasBufferGenerationError) return

            const oldDistancesInMeters =
                buffers?.map((entry) => ({
                    ...entry,
                    distance: convertDistance(entry.distance, distanceUnit, DistanceUnit.Meters),
                })) || []

            const range = JSON.parse(JSON.stringify(oldDistancesInMeters)) as BufferRange[]

            if (updates.buffer !== undefined)
                range[index].distance = convertDistance(
                    updates.buffer,
                    distanceUnit,
                    DistanceUnit.Meters
                )
            if (updates.color !== undefined) range[index].color = updates.color
            if (updates.width !== undefined) range[index].width = updates.width

            // If traits other than the distance have been updated, dispatch an action
            // to update the cached buffers' colors/widths/etc.
            if (updates.buffer === undefined) {
                dispatch(
                    updateCachedFirePerimeterBuffersProperties({
                        perimeterId: entity.id,
                        buffers: range,
                    })
                )
            }

            updateBuffersArray({ range })
        },
        [
            buffers,
            distanceUnit,
            updateBuffersArray,
            entity.polygons,
            entity.buffers?.unit,
            dispatch,
            entity.id,
        ]
    )

    const { createBuffer, loading: isGeneratingBuffer } = useAsyncCallback(
        {
            createBuffer: async () => {
                if (buffers && buffers.length >= 5) return

                const oldDistancesInMeters =
                    buffers?.map((entry) => ({
                        ...entry,
                        distance: convertDistance(
                            entry.distance,
                            distanceUnit,
                            DistanceUnit.Meters
                        ),
                    })) || []
                const previousBuffer = oldDistancesInMeters?.at(-1)?.distance || 0

                const color = nextBufferColor(buffers)

                let hasBufferGenerationError = false

                /**
                 * Given some delta distance in meters, performs a buffer generation calculation to
                 * determine if the distance is valid (i.e. does not go past the center of the polygon).
                 *
                 * Operates recursively (restricted by MAX_PERIMETER_BUFFER_ATTEMPTS), dividing the
                 * distance by 10 on each recursive call, to ultimately result with the largest possible
                 * order of magnitude of a buffer distance.
                 */
                const findValidBufferDistance = async (
                    distance = 100,
                    attempt = 1
                ): Promise<number | null> => {
                    const distanceDelta = convertDistance(
                        distance,
                        distanceUnit,
                        DistanceUnit.Meters
                    )

                    let newBufferInMeters = previousBuffer + distanceDelta

                    // In a case where the previousBuffer and the distanceDelta sum to zero, the server
                    // will reject the update since the buffer would take the same shape as the original
                    // perimeter. So, this adds the distanceDelta again, effectively performing the
                    // incremental logic, but "skipping" the increment at a distance of 0.
                    if (newBufferInMeters === 0) newBufferInMeters += distanceDelta

                    let validBufferDistance: number | null = newBufferInMeters

                    try {
                        await testNewBufferDistance(
                            entity.polygons,
                            {
                                color,
                                width: LineWidth.normal,
                                distance: newBufferInMeters,
                            },
                            (entity.buffers?.unit as DistanceUnit) || distanceUnit
                        )
                    } catch (error) {
                        if (
                            error === BufferError.DistanceTooLarge ||
                            error === BufferError.Unknown
                        ) {
                            if (attempt < MAX_PERIMETER_BUFFER_ATTEMPTS) {
                                validBufferDistance = await findValidBufferDistance(
                                    distance / 10,
                                    attempt + 1
                                )
                            } else {
                                dispatch(
                                    setGlobalNotificationContext({
                                        message: `A ${Math.round(convertDistance(newBufferInMeters, DistanceUnit.Meters, distanceUnit))} ${formattedDistanceUnit[distanceUnit].abbrev} buffer will exceed the limit.`,
                                        timeoutInMs: 4000,
                                        status: GlobalNotificationStatus.error,
                                    })
                                )

                                hasBufferGenerationError = true
                            }
                        } else {
                            hasBufferGenerationError = true
                        }
                    }

                    if (!hasBufferGenerationError) return validBufferDistance

                    return null
                }

                const newBufferInMeters = await findValidBufferDistance()

                if (newBufferInMeters === null) return

                updateBuffersArray({
                    range: [
                        ...oldDistancesInMeters,
                        {
                            distance: newBufferInMeters,
                            color,
                            width: LineWidth.normal,
                        },
                    ],
                    unit: distanceUnit,
                })
            },
        },
        [buffers, distanceUnit, entity.polygons, entity.buffers?.unit, dispatch, updateBuffersArray]
    )

    return {
        distanceUnit,
        unitAbbreviation,
        buffers,
        createBuffer,
        editBufferAtIndex,
        deleteBufferAtIndex,
        updateBuffersArray,
        isGeneratingBuffer,
        bufferDistancesBeingUpdated,
    }
}
